import { HttpErrorResponse } from '@angular/common/http';
import {
  EnvironmentProviders,
  ErrorHandler,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { UnleashClient } from 'unleash-proxy-client';

export interface SentryConfig {
  enabled?: boolean;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  unleash?: boolean;
}

const isDuplicateHttpError = (hint: Sentry.EventHint) => {
  const org = hint?.originalException;
  return (
    org instanceof HttpErrorResponse ||
    (typeof org === 'string' && org.startsWith('Http failure response for'))
  );
};

const IgnoreHttpClientUrlRegex = [
  /(.*)\/secure\/next\/api\/method\/(.*)\/notifications/,
];
const ignoreHttpClientError = (event: Sentry.Event) => {
  if (event.message?.startsWith('HTTP Client Error')) {
    const matched = IgnoreHttpClientUrlRegex.find((regex) =>
      regex.test(event.request?.url ?? ''),
    );

    return matched !== undefined;
  }

  return false;
};

export const initializeSentry = (dsn: string, config?: SentryConfig) => {
  Sentry.init({
    beforeSend(event, hint) {
      if (isDuplicateHttpError(hint) || ignoreHttpClientError(event)) {
        return null;
      }

      return event;
    },
    dsn,
    enabled: config?.enabled === true,
    environment: config?.environment,
    release: config?.release,
    integrations: [
      Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      ...(config?.unleash === true
        ? [Sentry.unleashIntegration({ featureFlagClientClass: UnleashClient })]
        : []),
    ],
    tracesSampleRate: config?.tracesSampleRate ?? 1.0,
    replaysSessionSampleRate: config?.replaysSessionSampleRate ?? 0.05,
    replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 0.1,
    sendDefaultPii: true,
  });
};

export const provideSentry: Array<Provider | EnvironmentProviders> = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  provideAppInitializer(() => {
    inject(Sentry.TraceService);
  }),
];

export const sentrySetUser = (userId: string | undefined) => {
  Sentry.setUser(
    userId
      ? {
          id: userId,
          username: userId,
        }
      : null,
  );
};
